/**
 * Contract for the "Today's Clinic" front-desk day view.
 *
 * Nothing in the current database backs this yet - there is no visit, token,
 * queue or order collection - so `api.clinicDay` is served from a mock. These
 * types are the single agreement between the screen and whatever eventually
 * implements it; the screen reads nothing else.
 *
 * Domain rules baked in here, so they survive the move to a real backend:
 *
 *  - The queue is keyed on `token`, not on the patient ID. A patient can be in
 *    the queue before a Patient ID exists, which is why `patientId` is nullable.
 *  - The doctor consult is an optional step. "Skipped" is a terminal outcome,
 *    not a pending one, so the flow must never block waiting on it.
 *  - Once a product is confirmed an order is raised and the patient leaves this
 *    view for the production pipeline; `confirmedOrder` is that hand-off.
 */

/** The eight stages every patient moves through, in order. */
export type FlowStepId =
	| "arrival"
	| "registration"
	| "doctor_consult"
	| "scan_assessment"
	| "additional_questions"
	| "recommendation"
	| "choose_product"
	| "billing";

export const FLOW_STEP_ORDER: FlowStepId[] = [
	"arrival",
	"registration",
	"doctor_consult",
	"scan_assessment",
	"additional_questions",
	"recommendation",
	"choose_product",
	"billing"
];

/** How the patient came to the clinic today. */
export type PatientSource =
	| { kind: "walk_in" }
	| { kind: "doctor_referral"; referrer: string }
	| { kind: "franchise_referral" };

/**
 * Consult state. `not_reached` means the patient has not got here yet, which is
 * distinct from `skipped` - the latter is a completed outcome.
 */
export type ConsultState =
	| { status: "not_reached" }
	| { status: "waiting" }
	| { status: "done"; doctor: string }
	| { status: "skipped" };

/** Who or what the patient is currently with: a staff member, a kiosk, a bay. */
export type Attendant = {
	name: string;
	detail: string | null;
};

/**
 * What the front desk should do next for this row. The choice of action belongs
 * to whoever owns the workflow rules, not to this screen, so it arrives decided.
 */
export type NextAction = {
	label: string;
	/**
	 * `technician` - a technician is mid-task on it (amber)
	 * `ready` - ready for the desk to move it on (green)
	 * `passive` - nothing to do yet, open to look (outline)
	 * `complete` - finished, view only (disabled)
	 */
	tone: "technician" | "ready" | "passive" | "complete";
};

export type QueuedPatient = {
	/** Queue identity, e.g. "T-014". Unique within the day. */
	token: string;
	name: string;
	/** Null until registration has issued one. */
	patientId: string | null;
	/** Null while the patient is still registering. */
	age: number | null;
	gender: "M" | "F" | "O" | null;
	/** False for a first visit, true for a returning patient. */
	isRepeat: boolean;
	source: PatientSource;
	consult: ConsultState;
	currentStep: FlowStepId;
	/** Free text qualifying the step, e.g. "kiosk" or "Step 2 of 3". */
	stepDetail: string | null;
	attendant: Attendant | null;
	/** Minutes waited at the current step; null once the patient is done. */
	waitingMinutes: number | null;
	/** Set only once billing has completed. Presence means the row is finished. */
	confirmedOrder: { orderNumber: string; amount: number } | null;
	nextAction: NextAction;
};

export type ClinicDaySummary = {
	registered: { count: number; walkIn: number; referred: number };
	awaitingConsult: { count: number };
	inScanning: { count: number; deviceLabel: string | null };
	choosingProduct: { count: number };
	/** `bookedAmount` is the total value of orders raised today, in rupees. */
	confirmedToday: { count: number; bookedAmount: number };
};

export type ClinicDaySnapshot = {
	clinic: { name: string; area: string | null };
	summary: ClinicDaySummary;
	queue: QueuedPatient[];
	/**
	 * Steps the clinic is actively working right now, used to highlight the flow
	 * strip. Anything earlier reads as done, anything later as pending.
	 */
	activeSteps: FlowStepId[];
};
